import { Checklist, PendingChecklist } from '../types';

interface ChecklistApprovalDialogProps {
  checklist: Checklist;
  metadata: PendingChecklist;
  approving: boolean;
  error: string | null;
  onCancel(): void;
  onApprove(): void;
}

export function ChecklistApprovalDialog({
  checklist,
  metadata,
  approving,
  error,
  onCancel,
  onApprove,
}: ChecklistApprovalDialogProps) {
  return (
    <div className="checklist-dialog-backdrop" role="presentation">
      <div
        className="checklist-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="approval-dialog-title"
      >
        <div className="checklist-dialog-header">
          <div>
            <h2 id="approval-dialog-title">Approve and publish checklist</h2>
            <p>Review the complete checklist before approving it.</p>
          </div>

          <button
            type="button"
            className="checklist-dialog-close"
            aria-label="Close approval dialog"
            disabled={approving}
            onClick={onCancel}
          >
            ×
          </button>
        </div>

        <div className="checklist-approval-warning">
          <strong>This action publishes the checklist.</strong>
          <ul>
            <li>
              It will be published for <strong>{metadata.standard}</strong>.
            </li>
            <li>Waiting assessment workflows will continue automatically.</li>
            <li>Normal editing will no longer be available.</li>
            <li>The approving user will be recorded.</li>
          </ul>
        </div>

        <div className="checklist-dialog-preview">
          <div className="checklist-preview-summary">
            <span className="checklist-requirement-count-badge">
              {checklist.requirements.length} requirement
              {checklist.requirements.length === 1 ? '' : 's'}
            </span>
          </div>

          {checklist.requirements.map((requirement, index) => (
            <section
              className="checklist-preview-requirement"
              key={requirement.requirement_id}
            >
              <h3>
                {index + 1}. {requirement.title}
              </h3>

              {requirement.description && (
                <p className="checklist-requirement-description">
                  {requirement.description}
                </p>
              )}

              <ol>
                {requirement.checklist.map((item, questionIndex) => (
                  <li key={`${requirement.requirement_id}-${questionIndex}`}>
                    <span className="checklist-question-preview">
                      {item.question}
                    </span>
                  </li>
                ))}
              </ol>
            </section>
          ))}
        </div>

        <div className="checklist-dialog-footer">
          {error && (
            <div
              className="checklist-dialog-error"
              role="alert"
              aria-live="polite"
            >
              {error}
            </div>
          )}

          <div className="checklist-dialog-actions">
            <button
              type="button"
              className="action-button secondary-button"
              disabled={approving}
              onClick={onCancel}
            >
              Continue editing
            </button>

            <button
              type="button"
              className={`action-button checklist-operation-button ${
                approving ? 'checklist-operation-button--busy' : ''
              }`}
              disabled={approving}
              aria-busy={approving}
              onClick={onApprove}
            >
              Approve and publish
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
